
import { Component, Vue } from 'vue-property-decorator'
import {
  previewBulkGrant,
  executeBulkGrant,
  getBulkGrantHistory,
  getBulkGrantCountries,
  getBulkGrantFeatures,
  getBulkGrantDeviceTypes
} from '@/api/aim'

interface CountryOption {
  countryCode: string
  userCount: number
}

interface FeatureOption {
  id: number
  name: string
  featureKey: string
}

interface DeviceTypeOption {
  deviceType: string
  deviceCount: number
}

interface PreviewResult {
  totalCount: number
  deviceCount?: number
  countryCounts: Array<{ countryCode: string; count: number }>
}

interface ExecuteResult {
  logId: number
  totalUsers: number
  successCount: number
  errorCount: number
  deviceSuccessCount?: number
  deviceErrorCount?: number
}

const countryNames: Record<string, string> = {
  // Europe
  TR: 'Turkey', GB: 'United Kingdom', DE: 'Germany', FR: 'France', IT: 'Italy',
  ES: 'Spain', NL: 'Netherlands', SE: 'Sweden', NO: 'Norway', DK: 'Denmark',
  FI: 'Finland', PL: 'Poland', AT: 'Austria', CH: 'Switzerland', BE: 'Belgium',
  PT: 'Portugal', GR: 'Greece', IE: 'Ireland', CZ: 'Czech Republic', HU: 'Hungary',
  RO: 'Romania', BG: 'Bulgaria', HR: 'Croatia', RS: 'Serbia', SI: 'Slovenia',
  SK: 'Slovakia', EE: 'Estonia', LV: 'Latvia', LT: 'Lithuania', UA: 'Ukraine',
  AL: 'Albania', AD: 'Andorra', BY: 'Belarus', BA: 'Bosnia', CY: 'Cyprus',
  IS: 'Iceland', LU: 'Luxembourg', MT: 'Malta', MD: 'Moldova', MC: 'Monaco',
  ME: 'Montenegro', GE: 'Georgia',
  // Russia & Central Asia
  RU: 'Russia', AZ: 'Azerbaijan', AM: 'Armenia', KG: 'Kyrgyzstan', UZ: 'Uzbekistan',
  MN: 'Mongolia',
  // East Asia & Pacific
  JP: 'Japan', KR: 'South Korea', CN: 'China', HK: 'Hong Kong', MO: 'Macau',
  TW: 'Taiwan',
  // South & Southeast Asia
  IN: 'India', PK: 'Pakistan', AF: 'Afghanistan', LK: 'Sri Lanka', MM: 'Myanmar',
  NP: 'Nepal', BD: 'Bangladesh', MY: 'Malaysia', SG: 'Singapore', TH: 'Thailand',
  VN: 'Vietnam', ID: 'Indonesia', PH: 'Philippines', KH: 'Cambodia',
  // Oceania
  AU: 'Australia', NZ: 'New Zealand', WS: 'Samoa', TV: 'Tuvalu',
  // Americas
  US: 'United States', CA: 'Canada', BR: 'Brazil', MX: 'Mexico', AR: 'Argentina',
  CL: 'Chile', CO: 'Colombia', PE: 'Peru', VE: 'Venezuela', CU: 'Cuba',
  EC: 'Ecuador', BO: 'Bolivia', PY: 'Paraguay', UY: 'Uruguay', GY: 'Guyana',
  CR: 'Costa Rica', PA: 'Panama', GT: 'Guatemala', SV: 'El Salvador', HN: 'Honduras',
  NI: 'Nicaragua', BZ: 'Belize', TT: 'Trinidad & Tobago', JM: 'Jamaica', BS: 'Bahamas',
  AW: 'Aruba',
  // Middle East
  SA: 'Saudi Arabia', AE: 'UAE', IL: 'Israel', JO: 'Jordan', LB: 'Lebanon',
  IQ: 'Iraq', SY: 'Syria', KW: 'Kuwait', OM: 'Oman', QA: 'Qatar', YE: 'Yemen',
  PS: 'Palestine', IR: 'Iran',
  // Africa
  EG: 'Egypt', ZA: 'South Africa', NG: 'Nigeria', GH: 'Ghana', KE: 'Kenya',
  ET: 'Ethiopia', DZ: 'Algeria', MA: 'Morocco', TN: 'Tunisia', LY: 'Libya',
  SN: 'Senegal', CI: 'Ivory Coast', BF: 'Burkina Faso', TG: 'Togo', BJ: 'Benin',
  MU: 'Mauritius', LR: 'Liberia', SL: 'Sierra Leone', CM: 'Cameroon', CG: 'Congo',
  CD: 'DR Congo', SC: 'Seychelles', SD: 'Sudan', RW: 'Rwanda', SO: 'Somalia',
  DJ: 'Djibouti', TZ: 'Tanzania', UG: 'Uganda', BI: 'Burundi', ZM: 'Zambia',
  MG: 'Madagascar', RE: 'Réunion', ZW: 'Zimbabwe', MW: 'Malawi', BW: 'Botswana',
  GM: 'Gambia', MR: 'Mauritania'
}

@Component({
  name: 'BulkGrant'
})
export default class extends Vue {
  private activeTab = 'new'

  // Filter options
  private countryOptions: CountryOption[] = []
  private featureOptions: FeatureOption[] = []
  private deviceTypeOptions: DeviceTypeOption[] = []

  // Filters
  private filters = {
    countries: [] as string[],
    featureIds: [] as number[],
    hasCane: null as boolean | null,
    deviceTypes: [] as string[]
  }

  // Preview
  private preview: PreviewResult | null = null
  private previewLoading = false
  private debounceTimer: any = null

  // Grant form
  private grantForm = {
    grantType: 'weassist' as 'weassist' | 'entitlement',
    grantTarget: 'user' as 'user' | 'device' | 'both',
    durationSeconds: 2700,
    expiresDate: '' as string | Date,
    description: '',
    grantFeatureId: null as number | null,
    grantDurationType: 'months',
    grantDurationValue: 1
  }

  // Execute
  private executeLoading = false
  private executeResult: ExecuteResult | null = null

  // History
  private historyList: any[] = []
  private historyLoading = false
  private historyPagination = {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 20
  }

  created() {
    this.loadOptions()
    this.fetchPreview()
  }

  private async loadOptions() {
    try {
      const [countriesRes, featuresRes, deviceTypesRes] = await Promise.all([
        getBulkGrantCountries(),
        getBulkGrantFeatures(),
        getBulkGrantDeviceTypes()
      ])

      if (countriesRes.data.status.code === 'success') {
        this.countryOptions = countriesRes.data.data
      }
      if (featuresRes.data.status.code === 'success') {
        this.featureOptions = featuresRes.data.data
      }
      if (deviceTypesRes.data.status.code === 'success') {
        this.deviceTypeOptions = deviceTypesRes.data.data
      }
    } catch (error) {
      console.error('Failed to load options', error)
    }
  }

  private onFilterChange() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.debounceTimer = setTimeout(() => {
      this.fetchPreview()
    }, 500)
  }

  private async fetchPreview() {
    this.previewLoading = true
    try {
      const response = await previewBulkGrant({
        countries: this.filters.countries,
        featureIds: this.filters.featureIds,
        hasCane: this.filters.hasCane,
        deviceTypes: this.filters.deviceTypes
      })

      if (response.data.status.code === 'success') {
        this.preview = response.data.data
      }
    } catch (error) {
      console.error('Preview failed', error)
    } finally {
      this.previewLoading = false
    }
  }

  private async handleExecute() {
    if (!this.preview || this.preview.totalCount === 0) return

    const isWeassist = this.grantForm.grantType === 'weassist'
    const targetLabel = this.grantForm.grantTarget === 'device'
      ? `${this.formatNumber(this.preview.deviceCount || 0)} devices`
      : this.grantForm.grantTarget === 'both'
        ? `${this.formatNumber(this.preview.totalCount)} users + ${this.formatNumber(this.preview.deviceCount || 0)} devices`
        : `${this.formatNumber(this.preview.totalCount)} users`
    const confirmMsg = isWeassist
      ? `${this.formatNumber(this.preview.totalCount)} users will receive ${Math.round(this.grantForm.durationSeconds / 60)} minutes of consult subscription. Are you sure?`
      : `${targetLabel} will receive a feature entitlement. Are you sure?`

    try {
      await this.$confirm(
        confirmMsg,
        'Confirm Bulk Grant',
        {
          confirmButtonText: 'Execute',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )
    } catch {
      return
    }

    this.executeLoading = true
    this.executeResult = null
    try {
      const payload: any = {
        countries: this.filters.countries,
        featureIds: this.filters.featureIds,
        hasCane: this.filters.hasCane,
        deviceTypes: this.filters.deviceTypes,
        grantType: this.grantForm.grantType,
        description: this.grantForm.description
      }

      if (isWeassist) {
        payload.durationSeconds = this.grantForm.durationSeconds
        payload.expiresDate = this.grantForm.expiresDate
      } else {
        payload.grantFeatureId = this.grantForm.grantFeatureId
        payload.grantDurationType = this.grantForm.grantDurationType
        payload.grantDurationValue = this.grantForm.grantDurationValue
        payload.grantTarget = this.grantForm.grantTarget
      }

      const response = await executeBulkGrant(payload)

      if (response.data.status.code === 'success') {
        this.executeResult = response.data.data
        this.$message.success(`Bulk grant completed: ${response.data.data.successCount} success, ${response.data.data.errorCount} errors`)
      } else {
        this.$message.error(response.data.status.message || 'Execution failed')
      }
    } catch (error: any) {
      const msg = error.response?.data?.status?.message || 'Execution failed'
      this.$message.error(msg)
      console.error(error)
    } finally {
      this.executeLoading = false
    }
  }

  private async fetchHistory() {
    this.historyLoading = true
    try {
      const response = await getBulkGrantHistory({
        page: this.historyPagination.currentPage,
        pageSize: this.historyPagination.pageSize
      })

      if (response.data.status.code === 'success') {
        this.historyList = response.data.data
        if (response.data.pagination) {
          this.historyPagination.totalCount = response.data.pagination.totalCount
          this.historyPagination.totalPages = response.data.pagination.totalPages
        }
      }
    } catch (error) {
      this.$message.error('Failed to load history')
      console.error(error)
    } finally {
      this.historyLoading = false
    }
  }

  // Watch active tab to load history on demand
  private handleTabClick() {
    if (this.activeTab === 'history' && this.historyList.length === 0) {
      this.fetchHistory()
    }
  }

  private formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return '0'
    return num.toLocaleString('en-US')
  }

  private formatMinutes(seconds: number): string {
    if (!seconds || seconds === 0) return '0 min'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (remainingSeconds === 0) {
      return `${minutes} min`
    }
    return `${minutes}m ${remainingSeconds}s`
  }

  private getCountryName(code: string): string {
    return countryNames[code] || code
  }

  private formatDate(date: string | undefined): string {
    if (!date) return '-'
    const d = new Date(date)
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  }
}
